import React from 'react';
import moment from 'moment';
import Editor from 'src/components/Editor/Editor';
import CategoryPill from 'src/components/CategoryPill/CategoryPill';
import Button from 'src/components/Button';
import {
  IBulkKeyPickupResponseSuiteKeyValue,
  IBulkKeyPickupResponseUnit,
} from 'src/services/client-types';
import BulkSummaryHeader from 'src/components/BulkOperations/_Shared/BulkSummaryHeader/BulkSummaryHeader';
import Callout from 'src/components/InformationCallout/Callout';
import Skeleton from 'src/components/Skeleton/Skeleton';
import CalcomConnectionBadge from 'src/components/CalcomConnectionBadge/CalcomConnectionBadge';
import { Link } from 'react-router-dom';
import { PATHS } from 'src/common/constants';
import {
  IUseCalcomConnection,
  useCalcomOauthConnection,
} from 'src/pages/Calendar/CalcomIntegration/CustomHooks/useCalcomOauthConnection';
import './BulkKeyPikcupSummary.scss';

interface BulkKeyPickupSummaryProps {
  createdKeyPickups: IBulkKeyPickupResponseUnit[];
  onClose: () => void;
}

const BulkKeyPickupSummary = ({
  createdKeyPickups,
  onClose,
}: BulkKeyPickupSummaryProps) => {
  const totalUnits: number = createdKeyPickups.length;
  const unitLabel: string = totalUnits === 1 ? 'unit' : 'units';

  const {
    isConnected: isCalcomConnected,
    isLoadingStatus: isLoadingCalcomStatus,
  }: IUseCalcomConnection = useCalcomOauthConnection();

  return (
    <div className="bulk-key-pickup-summary">
      <BulkSummaryHeader
        title="Key Pickup Configuration Complete!"
        subtitle={
          <span>
            Updated <strong>{totalUnits}</strong> {unitLabel}
          </span>
        }
        centered={false}
      />

      {isLoadingCalcomStatus && (
        <Callout
          variant="info"
          title=""
          subtitle={
            <>
              <Skeleton variant="text" width="100%" />
              <Skeleton variant="text" width="80%" />
            </>
          }
        >
          <div className="calcom-nudge">
            <Skeleton variant="rect" width="9rem" height="2.2rem" />
            <Skeleton variant="text" width="16rem" />
          </div>
        </Callout>
      )}

      {!isLoadingCalcomStatus && isCalcomConnected && (
        <Callout
          variant="info"
          title=""
          subtitle="You can now invite these homeowners to self-book their key pickups from your live Cal.com availability — head over whenever you're ready."
        >
          <div className="calcom-nudge">
            <CalcomConnectionBadge isConnected />
            <Link
              to={PATHS.CALENDAR_CALCOM_KEY_PICKUPS}
              target="_blank"
              rel="noopener noreferrer"
              className="calcom-cta"
            >
              Schedule key pickups with Cal.com →
            </Link>
          </div>
        </Callout>
      )}

      <div className="summary-content">
        <div className="units-list">
          {createdKeyPickups.map((unit: IBulkKeyPickupResponseUnit) => (
            <div key={unit.unitId} className="unit">
              <div className="unit-header">
                <span className="unit-title">{unit.number}</span>
                <span className="unit-id">{unit.unitId}</span>
              </div>

              {unit.keyHandOffInstructions && (
                <div className="field">
                  <span className="field-label">Instructions:</span>
                  <div className="field-content">
                    <div className="instructions">
                      <Editor
                        editorState={unit.keyHandOffInstructions}
                        onEditorStateChange={() => {}}
                        readOnly={true}
                        minHeight={80}
                        resizable={false}
                      />
                    </div>
                  </div>
                </div>
              )}

              {unit.suiteKeysAndValues?.length > 0 && (
                <div className="field">
                  <span className="field-label">Additional Details added:</span>
                  <div className="field-content">
                    <div className="key-pairs">
                      {unit.suiteKeysAndValues.map(
                        (kv: IBulkKeyPickupResponseSuiteKeyValue) => (
                          <CategoryPill
                            key={kv.id}
                            text={`${kv.key}: ${kv.value}`}
                            color="#eff6ff"
                            textColor="#1e40af"
                            size="normal"
                            enabled={true}
                            centerText={false}
                          />
                        )
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="timestamp">
                Updated {moment(unit.lastModifiedOn).format('MMM DD, h:mm A')}
              </div>
            </div>
          ))}
        </div>
      </div>

      <footer className="actions">
        <Button color="teal" size="small" label="Done" onClick={onClose} />
      </footer>
    </div>
  );
};

export default BulkKeyPickupSummary;
